import type { AmazonExperimentMetrics } from "@/types/ab-tests";

/** Parse a single CSV line respecting quoted fields (Amazon MYE exports). */
export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (c === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
      continue;
    }
    current += c;
  }
  result.push(current.trim());
  return result.map((s) => s.replace(/^"|"$/g, ""));
}

function normalizeMetricKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

function isMyeHeaderLine(line: string): boolean {
  const lower = line.toLowerCase();
  if (!lower.includes("metric")) return false;
  const cols = parseCsvLine(line);
  if (cols.length < 3) return false;
  const c1 = cols[1]?.replace(/\*+$/g, "").trim().toLowerCase() ?? "";
  const c2 = cols[2]?.replace(/\*+$/g, "").trim().toLowerCase() ?? "";
  const versionPair =
    c1.includes("version") && c1.includes("a") && c2.includes("version") && c2.includes("b");
  const armPair = c1.includes("arm") && c1.includes("a") && c2.includes("arm") && c2.includes("b");
  return versionPair || armPair;
}

function parseDecimal(s: string): number | null {
  const t = s.replace(/,/g, "").trim();
  if (t === "") return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

function parsePercentToDecimal(s: string): number | null {
  const t = s.replace(/,/g, "").trim().replace(/%$/, "");
  if (t === "") return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

function parseIntCell(s: string): number | null {
  const t = s.replace(/[^0-9-]/g, "");
  if (t === "" || t === "-") return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

function parseMoney(s: string): number | null {
  const t = s.replace(/[^0-9.-]/g, "");
  if (t === "" || t === "-") return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Client-side preview parser for Amazon MYE CSV (aligned with backend AmazonMyeReportParser).
 */
export function parseAmazonMyeCsv(csvText: string): AmazonExperimentMetrics | null {
  let text = csvText;
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;

  const headerIdx = lines.findIndex((l) => isMyeHeaderLine(l));
  if (headerIdx === -1) return null;

  const rows: Record<string, { a: string; b: string }> = {};
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    if (cols.length < 3) continue;
    const metricRaw = cols[0]?.replace(/^"|"$/g, "").trim() ?? "";
    if (!metricRaw) continue;
    const key = normalizeMetricKey(metricRaw);
    rows[key] = {
      a: (cols[1] ?? "").replace(/^"|"$/g, "").trim(),
      b: (cols[2] ?? "").replace(/^"|"$/g, "").trim(),
    };
  }

  const upvRow = rows["units per unique visitor"];
  const convRow = rows["conversion"];
  const soldRow = rows["units sold"];
  const salesRow = rows["sales"];
  const sampleRow = rows["sample size"];
  const ufsRow = rows["units sold from search"];
  const sfsRow = rows["sales from search"];

  if (!upvRow || !convRow || !soldRow || !salesRow || !sampleRow) return null;

  const upvA = parseDecimal(upvRow.a);
  const upvB = parseDecimal(upvRow.b);
  const convA = parsePercentToDecimal(convRow.a);
  const convB = parsePercentToDecimal(convRow.b);
  const unitsA = parseIntCell(soldRow.a);
  const unitsB = parseIntCell(soldRow.b);
  const salesA = parseMoney(salesRow.a);
  const salesB = parseMoney(salesRow.b);
  const sampleA = parseIntCell(sampleRow.a);
  const sampleB = parseIntCell(sampleRow.b);

  if (
    upvA === null ||
    upvB === null ||
    convA === null ||
    convB === null ||
    unitsA === null ||
    unitsB === null ||
    salesA === null ||
    salesB === null ||
    sampleA === null ||
    sampleB === null
  ) {
    return null;
  }

  const metrics: AmazonExperimentMetrics = {
    unitsPerUniqueVisitor: {
      a: upvA,
      b: upvB,
      diff: Math.round((upvA - upvB) * 1e6) / 1e6,
    },
    conversion: {
      a: `${(convA * 100).toFixed(1)}%`,
      b: `${(convB * 100).toFixed(1)}%`,
      diff: `${((convA - convB) * 100).toFixed(3)}%`,
    },
    unitsSold: {
      a: unitsA,
      b: unitsB,
      diff: unitsA - unitsB,
    },
    sales: {
      a: `$${salesA.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      b: `$${salesB.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      diff: `$${Math.abs(salesA - salesB).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    },
    sampleSize: {
      a: sampleA,
      b: sampleB,
      diff: sampleA - sampleB,
    },
  };

  if (ufsRow && (ufsRow.a !== "" || ufsRow.b !== "")) {
    const ufsA = parseIntCell(ufsRow.a);
    const ufsB = parseIntCell(ufsRow.b);
    if (ufsA !== null && ufsB !== null) {
      metrics.unitsSoldFromSearch = { a: ufsA, b: ufsB, diff: ufsA - ufsB };
    }
  }

  if (sfsRow && (sfsRow.a !== "" || sfsRow.b !== "")) {
    const sfsA = parseMoney(sfsRow.a);
    const sfsB = parseMoney(sfsRow.b);
    if (sfsA !== null && sfsB !== null) {
      metrics.salesFromSearch = {
        a: `$${sfsA.toLocaleString("en-US")}`,
        b: `$${sfsB.toLocaleString("en-US")}`,
        diff: `$${Math.abs(sfsA - sfsB).toLocaleString("en-US")}`,
      };
    }
  }

  return metrics;
}
